/* External */
import {
  createContext, useContext, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';

/* Ott-common */
import MqttComponent from '@dtvgo/mqtt';

/* Utils */
import platform from '~/utils/platform';
import { isLowEndDevice } from '~/utils/platform/isLowEndDevice';
import { authInterceptor, refreshIdToken } from '../../services/authService';
import { getAccessTokenData, getUserAccountFromIdToken } from '../../utils/accessTokenData';
import {
  APP_THEME,
  ENVIRONMENT,
  MQTT_FLAG,
  MQTT_REALTIME_ENTITLEMENT_FLAG,
  isMqttAuthV2Active,
} from '../../utils/env';

/* Other */
import { useIsLoggedIn } from '../UserProvider';

const MqttContext = createContext();

/* On low-end TVs the connection waits until the first page has settled: the
   mqtt chunk (~420 KB legacy) plus a TLS WebSocket handshake otherwise land in
   the middle of the home page's first render. Realtime entitlement updates
   arriving a few seconds later is harmless. */
const LOW_END_CONNECT_DELAY_MS = 15_000;

async function initializeMqtt() {
  let auth;
  let client;
  const host = import.meta.env.VITE_BACKEND_SERVICE_HOST;
  const mqttComponentInstance = new MqttComponent(authInterceptor);
  const { idToken } = await getAccessTokenData();
  const { customerId, iat } = getUserAccountFromIdToken(idToken);

  try {
    auth = await mqttComponentInstance.authMqtt(
      idToken,
      host,
      APP_THEME,
      platform.appName, // clientId
      ENVIRONMENT,
      isMqttAuthV2Active,
    );
    client = await mqttComponentInstance.connectMqtt(
      auth.clientId,
      auth.url,
      customerId,
      (iat * 1000), // miliseconds
      refreshIdToken,
      MQTT_REALTIME_ENTITLEMENT_FLAG,
      MQTT_FLAG,
    );
  } catch (error) {
    console.error(error);
  }

  return client;
}

function MqttProvider({ children }) {
  const [contextValue, setContextValue] = useState('');
  const isLoggedIn = useIsLoggedIn();

  useEffect(() => {
    if (!MQTT_FLAG || !isLoggedIn) return undefined;

    let cancelled = false;
    let client;
    const initialize = async () => {
      try {
        const result = await initializeMqtt();
        if (cancelled) {
          result?.end?.(true);
          return;
        }
        client = result;
        setContextValue(result);
      } catch (error) {
        console.error(error);
      }
    };

    const timer = setTimeout(initialize, isLowEndDevice() ? LOW_END_CONNECT_DELAY_MS : 0);

    /* Logout does not reload the page: without this the previous user's client
       stayed connected and subscribed to dgo/v1/<previous customer>/#, and a
       realtime-entitlement message for that account would call
       refreshIdToken() inside the NEW user's session. */
    return () => {
      cancelled = true;
      clearTimeout(timer);
      client?.end?.(true);
      setContextValue('');
    };
  }, [isLoggedIn]);

  return (
    <MqttContext.Provider value={contextValue}>
      {children}
    </MqttContext.Provider>
  );
}

export default MqttProvider;
export const useMqttContext = () => useContext(MqttContext);
export { MqttContext };

MqttProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
